class KiwibitFanReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-kiwibit-fans-track]');
    this.cards = this.track ? Array.from(this.track.querySelectorAll('[data-kiwibit-fans-card]')) : [];
    this.prevButton = this.querySelector('[data-kiwibit-fans-prev]');
    this.nextButton = this.querySelector('[data-kiwibit-fans-next]');
    this.progress = this.querySelector('[data-kiwibit-fans-progress]');
    this.frame = null;
    this.peekTimer = null;
    this.peekFrame = null;
    this.snapTimer = null;
    this.snapFrame = null;
    this.snapPoints = [];
    this.touching = false;
    this.basePaddingRight = null;
    this.lastProgressOffset = null;
    this.lastThumbWidth = null;
    this.mobileQuery = window.matchMedia('(max-width: 989.98px)');

    this.onPrevClick = this.scrollByCard.bind(this, -1);
    this.onNextClick = this.scrollByCard.bind(this, 1);
    this.onScroll = this.scheduleState.bind(this);
    this.onResize = this.handleResize.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);
    if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.track) this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    if (this.track) this.track.addEventListener('touchend', this.onTouchEnd);
    if (this.track) this.track.addEventListener('touchcancel', this.onTouchEnd);
    window.addEventListener('resize', this.onResize);

    this.handleResize();
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.peekTimer) window.clearTimeout(this.peekTimer);
    if (this.peekFrame) cancelAnimationFrame(this.peekFrame);
    if (this.snapTimer) window.clearTimeout(this.snapTimer);
    if (this.snapFrame) cancelAnimationFrame(this.snapFrame);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNextClick);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.track) this.track.removeEventListener('touchstart', this.onTouchStart);
    if (this.track) this.track.removeEventListener('touchend', this.onTouchEnd);
    if (this.track) this.track.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('resize', this.onResize);
  }

  handleResize() {
    this.snapPoints = [];
    this.updateMobileEndPadding();
    this.updateState();
  }

  updateMobileEndPadding() {
    if (!this.track) return;

    if (this.basePaddingRight === null) {
      const computed = window.getComputedStyle(this.track);
      this.basePaddingRight = Number.parseFloat(computed.paddingRight || '0') || 0;
    }

    if (!this.mobileQuery.matches || !this.cards.length) {
      this.track.style.paddingRight = '';
      return;
    }

    const lastCard = this.cards[this.cards.length - 1];
    const lastCardWidth = lastCard.getBoundingClientRect().width;
    const endPadding = Math.max(this.basePaddingRight, this.track.clientWidth - lastCardWidth);

    this.track.style.paddingRight = `${Math.round(endPadding)}px`;
  }

  scheduleState() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateState(true);
      this.scheduleSnapToNearest();
    });
  }

  getStepWidth() {
    if (!this.cards.length) return this.track ? this.track.clientWidth : 0;

    const card = this.cards[0];
    const cardWidth = card.getBoundingClientRect().width;
    const computed = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(computed.columnGap || computed.gap || '0') || 0;
    return cardWidth + gap;
  }

  getSnapPoints() {
    if (!this.track || !this.cards.length) return [0];

    this.updateMobileEndPadding();

    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const points = [];

    this.cards.forEach((card) => {
      const point = Math.max(0, Math.min(card.offsetLeft, maxScroll));

      if (!points.some((existingPoint) => Math.abs(existingPoint - point) < 2)) {
        points.push(point);
      }
    });

    if (!points.some((point) => Math.abs(point - maxScroll) < 2)) {
      points.push(maxScroll);
    }

    return points.sort((a, b) => a - b);
  }

  getNearestSnapPoint(points = this.snapPoints.length ? this.snapPoints : this.getSnapPoints()) {
    if (!this.track || !points.length) return 0;

    const currentScroll = this.track.scrollLeft;

    return points.reduce((nearestPoint, point) => {
      const nearestDistance = Math.abs(nearestPoint - currentScroll);
      const distance = Math.abs(point - currentScroll);
      return distance < nearestDistance ? point : nearestPoint;
    }, points[0]);
  }

  scrollByCard(direction) {
    if (!this.track) return;

    const points = this.getSnapPoints();
    const currentPoint = this.getNearestSnapPoint(points);
    const currentIndex = points.findIndex((point) => Math.abs(point - currentPoint) < 2);
    const nextIndex = Math.max(0, Math.min(points.length - 1, currentIndex + direction));
    const nextLeft = points[nextIndex] ?? this.track.scrollLeft + this.getStepWidth() * direction;

    this.track.scrollTo({
      left: nextLeft,
      behavior: 'smooth'
    });
  }

  handleTouchStart() {
    if (!this.mobileQuery.matches) return;

    if (this.snapTimer) {
      window.clearTimeout(this.snapTimer);
      this.snapTimer = null;
    }
    if (this.snapFrame) {
      cancelAnimationFrame(this.snapFrame);
      this.snapFrame = null;
    }

    this.touching = true;
    this.snapPoints = this.getSnapPoints();
  }

  handleTouchEnd() {
    if (!this.mobileQuery.matches) return;

    this.touching = false;
    this.scheduleSnapToNearest();
  }

  scheduleSnapToNearest() {
    if (!this.track || !this.mobileQuery.matches || this.touching) return;
    if (this.snapTimer) window.clearTimeout(this.snapTimer);

    this.snapTimer = window.setTimeout(() => {
      this.snapTimer = null;
      const targetLeft = this.getNearestSnapPoint();

      if (Math.abs(targetLeft - this.track.scrollLeft) > 1) {
        this.track.scrollTo({
          left: targetLeft,
          behavior: 'smooth'
        });
      }

      if (this.snapFrame) cancelAnimationFrame(this.snapFrame);
      this.snapFrame = requestAnimationFrame(() => {
        this.snapFrame = null;
        this.updateState();
      });
    }, 60);
  }

  updatePeekCards() {
    if (!this.track || !this.cards.length) return;

    const viewportRect = this.track.getBoundingClientRect();
    const edgeOffset = 1;

    this.cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      const isVisible = cardRect.right > viewportRect.left + edgeOffset && cardRect.left < viewportRect.right - edgeOffset;
      const isClipped = cardRect.left < viewportRect.left - edgeOffset || cardRect.right > viewportRect.right + edgeOffset;

      card.classList.toggle('kiwibit-fan-reviews__card--peek', isVisible && isClipped);
    });
  }

  schedulePeekCards() {
    if (this.peekTimer) window.clearTimeout(this.peekTimer);
    if (this.peekFrame) return;

    this.peekFrame = requestAnimationFrame(() => {
      this.peekFrame = null;
      this.updatePeekCards();
    });
  }

  updateState(deferPeek = false) {
    if (!this.track) return;

    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const currentScroll = this.track.scrollLeft;
    const atStart = currentScroll <= 1;
    const atEnd = currentScroll >= maxScroll - 1;
    const isMobile = this.mobileQuery.matches;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
    if (deferPeek && isMobile) {
      this.schedulePeekCards();
    } else {
      this.updatePeekCards();
    }

    if (!this.progress) return;

    const trackWidth = this.progress.parentElement ? this.progress.parentElement.clientWidth : 0;
    if (trackWidth <= 0 || maxScroll <= 1) {
      this.progress.style.width = '100%';
      this.progress.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    const visibleRatio = Math.min(this.track.clientWidth / this.track.scrollWidth, 1);
    const thumbWidth = isMobile ? 38 : Math.max(trackWidth * visibleRatio, 20);
    const travel = Math.max(trackWidth - thumbWidth, 0);
    const progress = Math.min(Math.max(currentScroll / maxScroll, 0), 1);
    const offset = Math.round(travel * progress * 100) / 100;

    if (this.lastThumbWidth !== thumbWidth) {
      this.lastThumbWidth = thumbWidth;
      this.progress.style.width = `${thumbWidth}px`;
    }

    if (this.lastProgressOffset !== offset) {
      this.lastProgressOffset = offset;
      this.progress.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
  }
}

if (!customElements.get('kiwibit-fan-reviews-carousel')) {
  customElements.define('kiwibit-fan-reviews-carousel', KiwibitFanReviewsCarousel);
}
